import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  activateSubscriptionItem,
  getPlanPrice,
  getSubscriptionInfo,
  subscribe,
} from "@/helper/contractFunctions/MafiaPlayerSubscription";
import { SwapToken } from "@/types/Contract/SwapToken";
import { getTokenBalance } from "@/helper/contractFunctions";
import { getSwapTokens } from "@/helper/contractFunctions/MafiaSwapRouter";
import { formatUnits } from "@/helper/viem";
import { giCreditPrice } from "@/helper/contractFunctions/MafiaGICredit";
import {
  buyPremium,
  claimKeys,
  getUserInfo,
} from "@/helper/contractFunctions/MafiaOGPremium";
import { UserInfo } from "@/types/Contract/Premium/UserInfo";

export interface PremiumState {
  legacyPremiumInfo: {
    userInfo: UserInfo;
    lastClaimedAt: number;
  };
  isBuyingLegacyPremium: boolean;
  isClaimingKeys: boolean;

  isSubcribing: boolean;
  isSubscribed: boolean;
  isGettingIsSubscribed: boolean;
  isGettingSubscriptionInfo: boolean;
  subscriptionInfo: any;

  swapTokenInfo: [SwapToken[], number[]];
  isLoadingSwapTokenInfo: boolean;

  swapTokenBalances: any;
  isLoadingSwapTokenBalances: boolean;

  planPrice: {
    plusPlanPrice: number;
    unlimitedPlanPrice: number;
  };
  isLoadingPlanPrice: boolean;

  giCreditPrice: number;
  isLoadingGiCreditPrice: boolean;
}

const initialState: PremiumState = {
  legacyPremiumInfo: {
    userInfo: { expireAt: 0, startedAt: 0 },
    lastClaimedAt: 0,
  },
  isBuyingLegacyPremium: false,
  isClaimingKeys: false,

  isSubcribing: false,
  isSubscribed: false,
  isGettingIsSubscribed: false,
  isGettingSubscriptionInfo: false,
  subscriptionInfo: null,

  swapTokenInfo: [[], []],
  isLoadingSwapTokenInfo: false,

  swapTokenBalances: {},
  isLoadingSwapTokenBalances: false,

  planPrice: {
    plusPlanPrice: 0,
    unlimitedPlanPrice: 0,
  },
  isLoadingPlanPrice: false,

  giCreditPrice: 0,
  isLoadingGiCreditPrice: false,
};

export const getUserPremiumInfo = createAsyncThunk(
  "premium/getUserPremiumInfo",
  async ({ account }: { account: string }) => getUserInfo(account)
);

export const buyPremiumAction = createAsyncThunk(
  "premium/buyLegacyPremium",
  async ({ account }: { account: string }) => buyPremium(account)
);

export const claimKeysAction = createAsyncThunk(
  "premium/claimKeys",
  async ({ account }: { account: string }) => {
    const data = await claimKeys(account);
    return data.transactionHash;
  }
);

export const activateSubscriptionItemAction = createAsyncThunk(
  "premium/activateSubscriptionItemAction",
  async ({ account, itemId }: { account: string; itemId: number }) => {
    const tx = await activateSubscriptionItem(account, itemId);
    return tx.transactionHash;
  }
);

export const subscribeAction = createAsyncThunk(
  "premium/subscribeAction",
  async ({
    swapTokenId,
    planType,
    tokenAddress,
    tokenAmount,
    account,
  }: {
    swapTokenId: number;
    planType: number;
    tokenAddress: string;
    tokenAmount: number;
    account: string;
  }) => {
    const tx = await subscribe(
      swapTokenId,
      tokenAddress,
      tokenAmount,
      planType,
      account
    );
    return tx.transactionHash;
  }
);

export const getSubscriptionInfoAction = createAsyncThunk(
  "premium/getSubscriptionInfo",
  async (account: string) => {
    const subscriptionInfo = await getSubscriptionInfo(account);
    console.log(subscriptionInfo);
    return subscriptionInfo;
  }
);

export const getSwapTokenBalance = createAsyncThunk(
  "premium/getSwapTokenBalance",
  async ({ account }: { account: string }, { getState }) => {
    const state = (getState() as any).premium as PremiumState;

    const balanceArray = await Promise.all(
      state.swapTokenInfo[0].map(async (token: SwapToken) => {
        const balance = await getTokenBalance(token.tokenAddress, account);
        return { [token.name]: balance };
      })
    );

    const balances = balanceArray.reduce((acc, entry) => {
      const key = Object.keys(entry)[0];
      acc[key] = entry[key];
      return acc;
    }, {});

    return balances;
  }
);

export const getSwapTokenInfo = createAsyncThunk(
  "premium/getSwapTokenInfo",
  async () => {
    const data = await getSwapTokens();

    const swapTokenList = data[0].map((tokenInfo, index) => {
      return {
        name: tokenInfo.name,
        tokenAddress: tokenInfo.tokenAddress,
        isStable: tokenInfo.isStable,
        isEnabled: tokenInfo.isEnabled,
        price: Number(tokenInfo.price),
        decimal: Number(tokenInfo.decimal),
        tokenId: index,
      } as SwapToken;
    });

    const swapTokenPrice = data[1].map((price, index) => {
      return Number(
        formatUnits(price, swapTokenList[index].decimal)
      );
    });

    return { swapTokenList, swapTokenPrice };
  }
);

export const getPlanPriceAction = createAsyncThunk(
  "premium/getPlanPriceAction",
  async () => {
    const planPrice = await getPlanPrice();
    return planPrice;
  }
);

export const getGiCreditPriceAction = createAsyncThunk(
  "premium/getGiCreditPriceAction",
  async () => {
    const price = await giCreditPrice();
    return price;
  }
);

export const premiumSlice = createSlice({
  name: "premium",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getUserPremiumInfo.fulfilled, (state, { payload }) => {
      state.legacyPremiumInfo = payload;
    });
    builder.addCase(buyPremiumAction.pending, (state) => {
      state.isBuyingLegacyPremium = true;
    });
    builder.addCase(buyPremiumAction.fulfilled, (state) => {
      state.isBuyingLegacyPremium = false;
    });
    builder.addCase(buyPremiumAction.rejected, (state) => {
      state.isBuyingLegacyPremium = false;
    });
    builder.addCase(claimKeysAction.pending, (state) => {
      state.isClaimingKeys = true;
    });
    builder.addCase(claimKeysAction.fulfilled, (state) => {
      state.isClaimingKeys = false;
    });
    builder.addCase(claimKeysAction.rejected, (state) => {
      state.isClaimingKeys = false;
    });

    builder.addCase(activateSubscriptionItemAction.pending, (state) => {
      state.isSubcribing = true;
    });
    builder.addCase(activateSubscriptionItemAction.fulfilled, (state) => {
      state.isSubcribing = false;
    });
    builder.addCase(activateSubscriptionItemAction.rejected, (state) => {
      state.isSubcribing = false;
    });

    builder.addCase(subscribeAction.pending, (state) => {
      state.isSubcribing = true;
    });
    builder.addCase(subscribeAction.fulfilled, (state) => {
      state.isSubcribing = false;
    });
    builder.addCase(subscribeAction.rejected, (state) => {
      state.isSubcribing = false;
    });

    builder.addCase(getSubscriptionInfoAction.pending, (state) => {
      state.isGettingSubscriptionInfo = true;
    });
    builder.addCase(
      getSubscriptionInfoAction.fulfilled,
      (state, { payload }) => {
        state.isGettingSubscriptionInfo = false;
        state.subscriptionInfo = payload;
      }
    );
    builder.addCase(getSubscriptionInfoAction.rejected, (state) => {
      state.isGettingSubscriptionInfo = false;
    });

    builder.addCase(getSwapTokenBalance.pending, (state) => {
      state.isLoadingSwapTokenBalances = true;
    });
    builder.addCase(getSwapTokenBalance.fulfilled, (state, { payload }) => {
      state.swapTokenBalances = payload;
      state.isLoadingSwapTokenBalances = false;
    });
    builder.addCase(getSwapTokenBalance.rejected, (state, { error }) => {
      state.isLoadingSwapTokenBalances = false;
      console.log(error);
    });

    builder.addCase(getSwapTokenInfo.pending, (state) => {});
    builder.addCase(getSwapTokenInfo.fulfilled, (state, { payload }) => {
      state.swapTokenInfo = [payload.swapTokenList, payload.swapTokenPrice];
    });
    builder.addCase(getSwapTokenInfo.rejected, (state, { error }) => {});

    builder.addCase(getPlanPriceAction.pending, (state) => {
      state.isLoadingPlanPrice = true;
    });
    builder.addCase(getPlanPriceAction.fulfilled, (state, { payload }) => {
      state.isLoadingPlanPrice = false;
      state.planPrice = {
        plusPlanPrice: payload.plusPlanPrice,
        unlimitedPlanPrice: payload.unlimitedPlanPrice,
      };
    });
    builder.addCase(getPlanPriceAction.rejected, (state, { error }) => {
      state.isLoadingPlanPrice = false;
      console.log(error);
    });

    builder.addCase(getGiCreditPriceAction.pending, (state) => {
      state.isLoadingGiCreditPrice = true;
    });
    builder.addCase(getGiCreditPriceAction.fulfilled, (state, { payload }) => {
      state.isLoadingGiCreditPrice = false;
      state.giCreditPrice = payload;
    });
    builder.addCase(getGiCreditPriceAction.rejected, (state, { error }) => {
      state.isLoadingGiCreditPrice = false;
      console.log(error);
    });
  },
});

export default premiumSlice.reducer;
